import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../../settings/tabs/types';
import { useIsNativeEmbed } from '../../../navigation/hooks/useIsNativeEmbed';
import { SettingsAccountRow } from '../settings/SettingsAccountRow';
import { SettingsCard } from '../settings/SettingsCard';
import { TikTokBlockNotes } from './TikTokBlockNotes';
import { TikTokConsentLine } from './TikTokConsentLine';
import { TikTokDisclosureFields } from './TikTokDisclosureFields';
import { TikTokInteractionFields } from './TikTokInteractionFields';
import { TikTokMediaLine } from './TikTokMediaLine';
import { TikTokPrivacyField } from './TikTokPrivacyField';
import { tiktokMessages } from './tiktokMessages';
import type { TikTokSettingsState } from './useTikTokSettings';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the settings sections; each part uses its own slice of it */
import styles from '../settings/PlatformSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerTikTokSectionProps {
  state: TikTokSettingsState;
  /** The length of the attached file, when the Composer knows it. */
  mediaDurationSec: number | null;
  hasMedia: boolean;
  /** The post buttons: the section repeats them under the consent line (design a2). */
  footer: React.ReactNode;
}

/**
 * The TikTok settings of a post (design music24-composer-tiktok a1 to a6; TikTok Content Sharing
 * Guidelines for Direct Post). It shows when TikTok is selected and the TikTok channel posts
 * through the API. The creator is read from TikTok each time the section opens.
 */
const ComposerTikTokSectionComponent: React.FC<ComposerTikTokSectionProps> = ({
  state,
  mediaDurationSec,
  hasMedia,
  footer,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isNativeEmbed = useIsNativeEmbed();
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const seePlans = useCallback(() => router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.subscription }), [router]);
  const { draft, answer, issues } = state;
  const creator = answer?.creator ?? null;
  const durationSec = mediaDurationSec ?? answer?.media?.durationSec ?? null;
  const { blockedReason } = state;
  const hint = issues.includes('disclosureEmpty')
    ? tiktokMessages.needsDisclosure
    : issues.includes('privacyMissing')
      ? tiktokMessages.needsPrivacy
      : null;

  return (
    <SettingsCard
      title={intl.formatMessage(tiktokMessages.title)}
      tag={intl.formatMessage(tiktokMessages.tag)}
      testId="tiktok-settings"
      footer={footer}
    >
      {creator === null && state.isLoading ? (
        <p className={styles.small} data-testid="tiktok-loading">
          {intl.formatMessage(tiktokMessages.loading)}
        </p>
      ) : null}
      {creator === null ? null : (
        <SettingsAccountRow
          eyebrow={intl.formatMessage(tiktokMessages.postingTo)}
          name={creator.nickname}
          handle={creator.username === null ? null : `@${creator.username}`}
          avatarUrl={creator.avatarUrl}
          note={intl.formatMessage(tiktokMessages.fromTikTok)}
        />
      )}
      <TikTokBlockNotes
        blockedReason={blockedReason}
        issues={issues}
        durationSec={durationSec}
        maxDurationSec={creator?.maxVideoPostDurationSec ?? null}
        canSeePlans={!isNativeEmbed}
        onCheckAgain={state.reload}
        onOpenChannels={openChannels}
        onSeePlans={seePlans}
      />
      {creator === null ? null : (
        <>
          <TikTokPrivacyField draft={draft} options={creator.privacyLevelOptions} onChange={state.setPrivacy} />
          <TikTokInteractionFields
            creator={creator}
            allowComment={draft.allowComment}
            allowDuet={draft.allowDuet}
            allowStitch={draft.allowStitch}
            mediaKind={hasMedia ? 'video' : null}
            onAllowComment={state.setAllowComment}
            onAllowDuet={state.setAllowDuet}
            onAllowStitch={state.setAllowStitch}
          />
          <TikTokDisclosureFields
            draft={draft}
            onDiscloseContent={state.setDiscloseContent}
            onYourBrand={state.setYourBrand}
            onBrandedContent={state.setBrandedContent}
          />
          <TikTokMediaLine durationSec={durationSec} maxDurationSec={creator.maxVideoPostDurationSec} />
          <TikTokConsentLine draft={draft} />
          {hint === null ? null : (
            <p className={styles.small} data-testid="tiktok-hint">
              {withBrandWord(intl.formatMessage(hint))}
            </p>
          )}
        </>
      )}
    </SettingsCard>
  );
};

export const ComposerTikTokSection = TypedMemo(ComposerTikTokSectionComponent);
